/**
 * Modal para mostrar el proceso manual de sincronización de HomeCenter
 */
import React, { useEffect } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { useHomeCenterManualProcess } from '../../hooks/cron/useHomeCenterManualProcess'

interface HomeCenterManualProcessModalProps {
  isOpen: boolean
  onClose: () => void
}

const HomeCenterManualProcessModal: React.FC<
  HomeCenterManualProcessModalProps
> = ({ isOpen, onClose }) => {
  const {
    isProcessing,
    currentPhase,
    progress,
    results,
    error,
    execute,
    reset,
  } = useHomeCenterManualProcess()

  // Ejecutar automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && currentPhase === 'idle' && !isProcessing) {
      execute()
    }
  }, [isOpen, currentPhase, isProcessing, execute])

  // Resetear al cerrar
  const handleClose = () => {
    if (!isProcessing) {
      reset()
      onClose()
    }
  }

  if (!isOpen) return null

  // Calcular porcentajes de progreso
  const ordersProgress =
    progress.totalPages > 0
      ? Math.round((progress.currentPage / progress.totalPages) * 100)
      : 0

  const datesProgress =
    progress.totalDates > 0
      ? Math.round((progress.processedDates / progress.totalDates) * 100)
      : 0

  // Función para obtener el icono de la fase actual
  const getPhaseIcon = () => {
    switch (currentPhase) {
      case 'fetching_orders':
        return (
          <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
        )
      case 'processing_dates':
        return (
          <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
        )
      case 'completed':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />
      case 'error':
        return <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
      default:
        return <ClockIcon className="h-8 w-8 text-gray-400" />
    }
  }

  // Función para obtener el texto de la fase actual
  const getPhaseText = () => {
    switch (currentPhase) {
      case 'fetching_orders':
        return 'Consultando pedidos...'
      case 'processing_dates':
        return 'Procesando fechas...'
      case 'completed':
        return 'Proceso completado'
      case 'error':
        return 'Error en el proceso'
      default:
        return 'Preparando...'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3>
            Proceso Manual de HomeCenter
          </h3>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className={`text-gray-500 hover:text-gray-700 ${
              isProcessing ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Estado actual */}
        <div className="flex items-center gap-3 mb-6">
          {getPhaseIcon()}
          <span className="text-lg font-medium">{getPhaseText()}</span>
        </div>

        {/* Fase 1: Consultando pedidos */}
        {currentPhase === 'fetching_orders' && (
          <div className="space-y-4 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Consultando páginas de pedidos...
                </span>
                <span className="text-sm text-gray-500">
                  {progress.currentPage} de {progress.totalPages} páginas
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${ordersProgress}%` }}
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">
                    Pedidos encontrados:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.ordersFetched}
                  </span>
                </div>
                {progress.oldestDate && (
                  <div>
                    <span className="font-medium text-blue-800">
                      Fecha más antigua:
                    </span>
                    <span className="ml-2 text-blue-700">
                      {progress.oldestDate}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fase 2: Procesando fechas */}
        {currentPhase === 'processing_dates' && (
          <div className="space-y-4 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Procesando fechas por tipoOrden...
                </span>
                <span className="text-sm text-gray-500">
                  TipoOrden {progress.currentTipoOrden} - Fecha{' '}
                  {progress.currentDate} ({datesProgress}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${datesProgress}%` }}
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">
                    TipoOrden actual:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.currentTipoOrden || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">
                    Fecha actual:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.currentDate || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">
                    Fechas procesadas:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.processedDates} de {progress.totalDates}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">
                    TipoOrdenes procesados:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.processedTipoOrdenes} de{' '}
                    {progress.totalTipoOrdenes}
                  </span>
                </div>
                {progress.startDate && (
                  <div className="col-span-2">
                    <span className="font-medium text-blue-800">
                      Fecha inicial calculada:
                    </span>
                    <span className="ml-2 text-blue-700">
                      {progress.startDate} (pedido más viejo - 2 días)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-red-800 block mb-1">
                  Error en el Proceso
                </span>
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Fase 3: Resultados */}
        {currentPhase === 'completed' && (
          <div className="space-y-6">
            {/* Resumen de resultados */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">
                  {results.summary.totalDates}
                </div>
                <div className="text-sm text-blue-700">Total Fechas</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-600">
                  {results.summary.totalCreated}
                </div>
                <div className="text-sm text-green-700">Actualizadas</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-600">
                  {results.summary.totalSkipped}
                </div>
                <div className="text-sm text-yellow-700">Saltadas</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-2xl font-bold text-red-600">
                  {results.summary.totalErrors}
                </div>
                <div className="text-sm text-red-700">Errores</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-600">
                  {results.summary.totalDetalles}
                </div>
                <div className="text-sm text-gray-700">Total Detalles</div>
              </div>
            </div>

            {/* Resultados por tipoOrden */}
            {Object.keys(results.byTipoOrden).length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  Resultados por TipoOrden
                </h4>
                {Object.entries(results.byTipoOrden).map(
                  ([tipoOrden, data]) => (
                    <div
                      key={tipoOrden}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                    >
                      <h5 className="font-semibold text-gray-700 mb-2">
                        TipoOrden {tipoOrden}
                      </h5>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Fechas:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {data.processedDates} de {data.totalDates}
                          </span>
                        </div>
                        <div>
                          <span className="text-green-600">Actualizadas:</span>
                          <span className="ml-2 font-medium text-green-700">
                            {data.totalCreated}
                          </span>
                        </div>
                        <div>
                          <span className="text-yellow-600">Saltadas:</span>
                          <span className="ml-2 font-medium text-yellow-700">
                            {data.totalSkipped}
                          </span>
                        </div>
                        <div>
                          <span className="text-red-600">Errores:</span>
                          <span className="ml-2 font-medium text-red-700">
                            {data.totalErrors}
                          </span>
                        </div>
                      </div>
                      {(data.detalles?.length ?? 0) > 0 && (
                        <div className="mt-3 max-h-32 overflow-y-auto">
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Detalles:</span>{' '}
                            {data.detalles?.length ?? 0} registros
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {/* Mensaje si no hay resultados */}
            {results.summary.totalDates === 0 && (
              <div className="text-center py-8">
                <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-lg text-gray-600">
                  No se encontraron fechas para procesar
                </p>
              </div>
            )}
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 mt-6">
          {currentPhase === 'completed' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Cerrar
            </button>
          )}

          {currentPhase === 'error' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={execute}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Reintentar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default HomeCenterManualProcessModal

